/**
 * FlexibleCRUD entity definition for AI Studio Agents catalog (S1).
 * Uses camelCase keys to match aiStudioAgentService mapAgent output.
 * Single State column = visibility (draft | private | public | archived).
 */

import React from 'react';
import { EntityDefinition } from '@/types/crud';
import { AgentStateBadge } from '@/components/ai-studio/agents/AgentStateBadge';
import type { StudioAgentState } from '@/types/aiStudioAgent';

export const aiStudioAgentEntity: EntityDefinition = {
  name: 'aiStudioAgents',
  displayName: 'Agents',
  displayNameSingular: 'Agent',
  apiEndpoint: '/ai-studio/agents',
  routePath: '/ai-studio/agents',
  idField: 'id',
  nameField: 'name',
  searchable: true,
  sortable: true,
  pageSize: 20,
  copyable: false,
  deletable: false,
  searchFields: ['code', 'name', 'domain'],
  defaultSort: { field: 'updatedAt', direction: 'desc' },
  fields: [
    { key: 'id', label: 'ID', type: 'text', hidden: true, order: 0 },
    { key: 'code', label: 'Code', type: 'text', readonly: true, order: 1 },
    { key: 'name', label: 'Name', type: 'text', required: true, order: 2 },
    { key: 'domain', label: 'Domain', type: 'text', order: 3 },
    {
      key: 'isPlatform',
      label: 'Scope',
      type: 'boolean',
      order: 4,
      render: (value: boolean) => (value ? 'Platform' : 'Tenant'),
    },
    {
      key: 'visibility',
      label: 'State',
      type: 'select',
      order: 5,
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'private', label: 'Private' },
        { value: 'public', label: 'Public' },
        { value: 'archived', label: 'Archived' },
      ],
    },
    { key: 'lifecycleStatus', label: 'Lifecycle', type: 'text', hidden: true, order: 6 },
    { key: 'updatedAt', label: 'Updated', type: 'datetime', order: 7 },
  ],
  tableColumns: [
    { key: 'code', label: 'Code', sortable: true },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'domain', label: 'Domain', sortable: true },
    {
      key: 'isPlatform',
      label: 'Scope',
      sortable: true,
      render: (value: boolean) => (value ? 'Platform' : 'Tenant'),
    },
    {
      key: 'tenantName',
      label: 'Tenant',
      sortable: true,
      render: (value: unknown, item?: Record<string, unknown>) =>
        item?.['isPlatform'] ? '—' : (typeof value === 'string' && value ? value : '—'),
    },
    {
      key: 'visibility',
      label: 'State',
      sortable: true,
      render: (value: unknown) => (value ? React.createElement(AgentStateBadge, { state: value as StudioAgentState }) : '—'),
    },
    {
      key: 'createdByName',
      label: 'Created by',
      sortable: true,
      render: (value: string | null | undefined) => value || '—',
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (value: string) =>
        value ? new Date(value).toLocaleString() : '—',
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      sortable: true,
      render: (value: string) =>
        value ? new Date(value).toLocaleString() : '—',
    },
  ],
};
